import { PropsWithChildren } from "react"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { MenuItem, Stack, Tooltip, Typography } from "@mui/material"
import {
    INVALID_INPUT_KEY,
    MAX_INPUT_REFILL_LENGTH,
} from "../../../../../modules/nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import { DynamicConfigField } from "./formTypes"
import { SelectedMainUserState } from "../../createOrderTypes"
import { DynamicAutocomplete } from "./DynamicAutocomplete"
import { sanitizeAndLimitInput } from "./helpers"
import {
    FormikCheckbox,
    FormikDateTimePicker,
    FormikDropdown,
    FormikSwitch,
    FormikTextField,
} from "../../../forms/Formik"

const useStyles = makeStyles()((theme) => ({
    switchWrap: {
        borderRadius: ".75rem",
        padding: ".75rem",
        background: theme.palette.tailwind.blue[50],

        "& label.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "flex-start",
        },
        "& .MuiFormControl-root": {
            width: "auto",
            flex: "none",
        },
    },
    switchDescription: {
        color: theme.palette.text.secondary,
    },

    icon: {
        color: theme.palette.tailwind.gray[500],
    },
}))

type DynamicFormFieldGroupWrapProps = Readonly<PropsWithChildren<DynamicFormFieldProps>>

export const DynamicFormFieldGroupWrap = ({ config, children }: DynamicFormFieldGroupWrapProps) => {
    return (
        <Tooltip title={config?.description && config?.label ? config.description : ""} arrow>
            <Stack gap=".5rem">{children}</Stack>
        </Tooltip>
    )
}
type DynamicFormFieldProps = {
    config: DynamicConfigField
    user?: SelectedMainUserState
    updateTextfieldClassName?: string
}

export function DynamicFormField({ config, user, updateTextfieldClassName }: DynamicFormFieldProps) {
    const { key, inputField, active, label, description } = config

    if (!inputField || !active) return null

    const { classes } = useStyles()

    const { type, isRequired, maxLength, maxValue, isMultiline, data, predefined } = inputField

    const mainLabel = (label || description || "") + (isRequired ? " *" : "")

    switch (type) {
        case "textField":
        case "number":
            return (
                <DynamicFormFieldGroupWrap config={config}>
                    <FormikTextField
                        name={key as string}
                        label={label || description}
                        type={type}
                        multiline={isMultiline}
                        fullWidth
                        inputProps={
                            type === "textField"
                                ? { maxLength: maxLength }
                                : {
                                      inputMode: "numeric",
                                      onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                          const sanitized = sanitizeAndLimitInput(
                                              e.currentTarget.value,
                                              maxLength || MAX_INPUT_REFILL_LENGTH,
                                              maxValue,
                                          )
                                          if (e.currentTarget.value !== sanitized) {
                                              e.currentTarget.value = sanitized
                                          }
                                      },
                                      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                          if (INVALID_INPUT_KEY.includes(e.key)) {
                                              e.preventDefault()
                                          }
                                      },
                                  }
                        }
                        InputLabelProps={{
                            required: isRequired,
                        }}
                        className={updateTextfieldClassName}
                        size="small"
                    />
                </DynamicFormFieldGroupWrap>
            )

        case "checkbox":
            return <FormikCheckbox name={key as string}>{description}</FormikCheckbox>

        case "select":
        case "singleSelect":
            const options = config.selectValues?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))

            const SelectContent = (
                <FormikDropdown fullWidth name={key as string} label={mainLabel} size="small">
                    {options}
                </FormikDropdown>
            )

            return type === "select" ? (
                SelectContent
            ) : (
                <DynamicFormFieldGroupWrap config={config}>{SelectContent}</DynamicFormFieldGroupWrap>
            )

        case "dateTimePicker":
        case "dateTime":
            return (
                <DynamicFormFieldGroupWrap config={config}>
                    <FormikDateTimePicker
                        name={key as string}
                        label={mainLabel}
                        format={data}
                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                    />
                </DynamicFormFieldGroupWrap>
            )

        case "label":
            return <Typography>{predefined?.text || description}</Typography>

        case "component":
            return (
                <Typography variant="body2" color="textSecondary">
                    {description}
                </Typography>
            )

        case "toogle":
            return (
                <Stack flexDirection="row" gap=".5rem" justifyContent="space-between" className={classes.switchWrap}>
                    <Stack flexDirection="row" alignItems="center" gap=".5rem">
                        <Typography variant="subtitle1">{label}</Typography>
                        {description && (
                            <Tooltip title={description} arrow>
                                <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                            </Tooltip>
                        )}
                    </Stack>

                    <FormikSwitch name={key as string} />
                </Stack>
            )
        case "autocomplete":
            return (
                <DynamicFormFieldGroupWrap config={config}>
                    <DynamicAutocomplete config={config} user={user} />
                </DynamicFormFieldGroupWrap>
            )

        default:
            return null
    }
}
